/**
 * Overview Tab Component
 * Displays ghost application overview information
 */

import React from 'react';
import {
  Box,
  Typography,
  Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatDateTime } from '../../../../../utils/dateUtils';
import type { GhostApplicationDetail } from '../../../../../types/ghostVerification';

interface OverviewTabProps {
  application: GhostApplicationDetail;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ application }) => {
  const theme = useTheme();

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
        {/* Overview Information Card */}

        <Grid container spacing={3}>

          {/* Call Sign */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                marginBottom: theme.spacing(1),
                color: theme.palette.text.secondary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              Call Sign
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.primary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              {application.applicantName || 'N/A'}
            </Typography>
          </Grid>

          {/* Email */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                marginBottom: theme.spacing(1),
                color: theme.palette.text.secondary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              Email
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.primary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              {application.email || 'N/A'}
            </Typography>
          </Grid>

          {/* Phone Number */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                marginBottom: theme.spacing(1),
                color: theme.palette.text.secondary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              Phone Number
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.primary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              {application.phoneNumber || 'N/A'}
            </Typography>
          </Grid>

          {/* Submission Date */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                marginBottom: theme.spacing(1),
                color: theme.palette.text.secondary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              Submission Date
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.primary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              {formatDateTime(application.submittedOn)}
            </Typography>
          </Grid>

          {/* Ghost Tier - Only show if status is approved */}
          {application.verificationStatus === 'Approved' && application.ghostTier && (
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  marginBottom: theme.spacing(1),
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                Ghost Tier
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: theme.typography.body2.fontSize,
                  fontWeight: 500,
                }}
              >
                {application.ghostTier}
              </Typography>
            </Grid>
          )}

          {/* Reviewed By */}
          {application.reviewedBy && (
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  marginBottom: theme.spacing(1),
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                Reviewed By
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                {application.reviewedBy}
              </Typography>
            </Grid>
          )}

          {/* Reviewed At */}
          {application.reviewedAtText && (
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  marginBottom: theme.spacing(1),
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                Reviewed At
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                {application.reviewedAtText}
              </Typography>
            </Grid>
          )}

          {/* Review Notes */}
          {application.reviewNotes && (
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  marginBottom: theme.spacing(1),
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                Review Notes
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: theme.typography.body2.fontSize,
                  lineHeight: 1.6,
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.customColors.searchBackground,
                  borderRadius: theme.shapes.borderRadius.md,
                }}
              >
                {application.reviewNotes}
              </Typography>
            </Grid>
          )}

          {/* Rejection Reason */}
          {application.rejectionReason && (
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  marginBottom: theme.spacing(1),
                  color: theme.palette.error.main,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                Rejection Reason
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.error.main,
                  fontSize: theme.typography.body2.fontSize,
                  lineHeight: 1.6,
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(244, 67, 54, 0.2)'
                    : 'rgba(244, 67, 54, 0.1)',
                  borderRadius: theme.shapes.borderRadius.md,
                }}
              >
                {application.rejectionReason}
              </Typography>
            </Grid>
          )}

          {/* Verification Notes - Only show if status is rejected */}
          {application.verificationStatus === 'Rejected' && application.verificationNotes && (
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  marginBottom: theme.spacing(1),
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                Verification Notes
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: theme.spacing(2),
                  padding: theme.spacing(2),
                  backgroundColor: theme.customColors.searchBackground,
                  border: `1px solid ${theme.customColors.tableBorder}`,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.primary,
                      fontSize: theme.typography.body2.fontSize,
                      lineHeight: 1.6,
                    }}
                  >
                    {application.verificationNotes}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>

      </Box>
    </>
  );
};

